<template>
  <v-container>
    <v-responsive
      class="align-center mx-auto"
      max-width="1000"
    >
      <h1 class="text-h4 font-weight-black pt-8 pb-4 pl-3">
        Product Documents
      </h1>

      <v-alert
        v-if="error"
        color="error"
        icon="mdi-alert-circle"
        title="Error Loading Documents"
        variant="tonal"
        border="start"
        class="mb-4"
        closable
        @click:close="error = null"
      >
        {{ error }}
      </v-alert>

      <v-card
        class="rounded-xl"
        elevation="0"
      >
        <v-card-title class="d-block pa-6">
          <SearchField
            v-model="search"
            placeholder="Search documents..."
            class="mb-6"
          />
          
          <TagFilter
            v-model="selectedTags"
            :available-tags="availableTags"
          />
        </v-card-title>

        <v-divider />

        <v-data-table
          :headers="headers"
          :items="filteredDocuments"
          :search="search"
          :loading="loading"
          hover
          class="document-table"
        >
          <template #[`item.title`]="{ item }">
            <v-btn
              variant="text"
              color="primary"
              class="pa-0 text-none text-body-1"
              @click="handleDocumentDownload(item)"
            >
              {{ item.title }}
            </v-btn>
          </template>

          <template #[`item.size`]="{ item }">
            <span class="text-medium-emphasis">
              {{ formatFileSize(item.file.size) }}
            </span>
          </template>

          <template #[`item.tags`]="{ item }">
            <v-chip
              v-for="tag in item.tags"
              :key="tag.id"
              class="me-2"
              size="small"
              variant="tonal"
              color="primary"
            >
              {{ tag.name }}
            </v-chip>
          </template>

          <template #[`item.created`]="{ item }">
            <span class="text-medium-emphasis">
              {{ new Date(item.created!).toLocaleDateString() }}
            </span>
          </template>

          <template #[`item.modified`]="{ item }">
            <span class="text-medium-emphasis">
              {{ new Date(item.modified!).toLocaleDateString() }}
            </span>
          </template>
        </v-data-table>
      </v-card>
    </v-responsive>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
    >
      {{ snackbar.text }}
    </v-snackbar>
  </v-container>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useDocumentStore } from '@/stores/document'
import type { ITag } from '@/models/tag/tag.types'
import { SystemError } from '@/models/error'
import TagFilter from '@/components/common/TagFilter.vue'
import SearchField from '@/components/common/SearchField.vue'
import { downloadFile, formatFileSize } from '@/utils/file'
import type { IDocument } from '@/models/document'
import { analyticsService } from '@/services/analytics'

const documentStore = useDocumentStore()
const { getDocuments } = storeToRefs(documentStore)

const search = ref('')
const loading = ref(true)
const error = ref<string | null>(null)
const selectedTags = ref<number[]>([])

const snackbar = ref({
  show: false,
  text: '',
  color: 'success'
})

// Get unique tags from all documents
const availableTags = computed(() => {
  const tagMap = new Map<number, ITag>()
  getDocuments.value?.forEach(doc => {
    doc.tags?.forEach(tag => {
      if (tag.id) {
        tagMap.set(tag.id, tag)
      }
    })
  })
  return Array.from(tagMap.values())
})

// Filter documents based on selected tags
const filteredDocuments = computed(() => {
  if (!selectedTags.value.length) return getDocuments.value

  return getDocuments.value?.filter(doc => 
    doc.tags?.some(tag => tag.id && selectedTags.value.includes(tag.id))
  )
})

const headers = [
  { 
    title: 'Title',
    key: 'title',
    align: 'start' as const,
    width: '40%'
  },
  { 
    title: 'Size',
    key: 'size',
    align: 'start' as const,
    width: '100px'
  },
  { 
    title: 'Tags',
    key: 'tags',
    align: 'start' as const,
    width: '25%'
  },
  { 
    title: 'Created',
    key: 'created',
    align: 'start' as const,
    width: '120px'
  },
  { 
    title: 'Modified',
    key: 'modified',
    align: 'start' as const,
    width: '120px'
  }
]

async function fetchDocuments() {
  try {
    loading.value = true
    error.value = null
    await documentStore.fetchDocuments()
  } catch (e) {
    if (e instanceof SystemError) {
      error.value = e.detail || e.message
    } else {
      error.value = 'Failed to load documents'
    }
  } finally {
    loading.value = false
  }
}

async function handleDocumentDownload(document: IDocument) {
  try {
    await downloadFile(document.file, document.title)

    // TODO: Add document type - tags
    analyticsService.track('Product Document Downloaded', {
      'Document Name': document.title,
    })

    // Show success snackbar
    snackbar.value = {
      show: true,
      text: `${document.title} downloaded successfully`,
      color: 'success'
    }
  } catch (e) {
    console.error('Download failed:', e)
    error.value = 'Failed to download product document'
  }
}

onMounted(() => {
  fetchDocuments()
})
</script>

<style scoped>
.v-card-title {
  padding: 24px 24px 16px;
}

:deep(.document-table) {
  .v-data-table-header__content {
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface));
  }

  .v-data-table__tr {
    transition: all 0.2s ease-in-out;
    
    &:hover {
      .v-btn {
        color: rgb(var(--v-theme-primary));
        text-decoration: underline;
      }
    }
  }

  .v-data-table__td {
    padding-top: 12px;
    padding-bottom: 12px;
  }

  .v-chip {
    font-weight: 500;
  }
}

:deep(.v-card) {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  
  .v-table {
    background: transparent;
  }
}

:deep(.v-alert) {
  border-radius: 8px;
}
</style>
